import { Typography } from "antd";
import LegalPageLayout from "./LegalPageLayout";

const { Title, Paragraph, Text } = Typography;

export default function TermsOfServicePage() {
  return (
    <LegalPageLayout title="Terms of Service" lastUpdated="October 22, 2026">
      <Paragraph>
        These Terms of Service ("Terms") govern access to and use of Hairplane's salon and barbershop management
        platform (the "Service") by the business registering for an account ("you" or "Business Customer"). By
        creating an account, you agree to these Terms.
      </Paragraph>

      <Title level={4}>1. The Service</Title>
      <Paragraph>
        Hairplane provides software for scheduling, point-of-sale, client management, staff management, and
        related tools for salon and barbershop businesses. We may add, change, or remove features over time.
      </Paragraph>

      <Title level={4}>2. Account Registration</Title>
      <Paragraph>
        You must provide accurate information when creating an account and keep your login credentials secure.
        You're responsible for all activity under your account, including activity by staff accounts you create.
      </Paragraph>

      <Title level={4}>3. Subscription, Fees, and Billing</Title>
      <Paragraph>
        Paid plans, where applicable, renew automatically for successive billing periods unless cancelled before
        the renewal date. We'll clearly disclose the price, billing frequency, and cancellation method before you
        subscribe. You can cancel at any time through your account settings; cancellation takes effect at the end
        of the current billing period, and we don't provide prorated refunds for partial periods except where
        required by law.
      </Paragraph>

      <Title level={4}>4. Your Responsibilities</Title>
      <Paragraph>
        You're responsible for the accuracy of information you enter into the Service, including your Clients'
        contact information. If you use the Service's messaging features (SMS or email) to contact your Clients,
        <Text strong> you are responsible for obtaining any consent required by law</Text> (including under the
        Telephone Consumer Protection Act for text messages, and the CAN-SPAM Act for email) before sending
        messages, and for honoring opt-out requests. Hairplane provides the tools; you control who is contacted
        and what is sent.
      </Paragraph>

      <Title level={4}>5. Your Clients' Data</Title>
      <Paragraph>
        As between you and Hairplane, you are the data controller for information about your own Clients that you
        or they enter into the Service, and Hairplane acts as a service provider processing that data on your
        behalf and under your instructions. You're responsible for having a lawful basis to collect and share
        that information with us.
      </Paragraph>

      <Title level={4}>6. Payments</Title>
      <Paragraph>
        Card payments processed through the Service are handled by a third-party payment processor. We don't
        store full card numbers. You're responsible for complying with that processor's terms and any applicable
        payment network rules.
      </Paragraph>

      <Title level={4}>7. Prohibited Uses</Title>
      <Paragraph>
        You agree not to use the Service to violate any law, send unsolicited or non-consensual communications,
        infringe anyone's rights, upload malicious code, or attempt to gain unauthorized access to the Service or
        other accounts.
      </Paragraph>

      <Title level={4}>8. Intellectual Property</Title>
      <Paragraph>
        We own the Service and all associated software, design, and trademarks. You retain ownership of the data
        you enter into the Service. We grant you a limited, non-exclusive, non-transferable license to use the
        Service for your business during your subscription.
      </Paragraph>

      <Title level={4}>9. Termination</Title>
      <Paragraph>
        You may stop using the Service and close your account at any time. We may suspend or terminate accounts
        that violate these Terms, with notice where reasonably possible.
      </Paragraph>

      <Title level={4}>10. Disclaimers</Title>
      <Paragraph>
        The Service is provided "as is" and "as available," without warranties of any kind, express or implied,
        including merchantability, fitness for a particular purpose, and non-infringement. We don't guarantee the
        Service will be uninterrupted or error-free.
      </Paragraph>

      <Title level={4}>11. Limitation of Liability</Title>
      <Paragraph>
        To the maximum extent permitted by law, Hairplane will not be liable for indirect, incidental, special,
        consequential, or punitive damages, or for lost profits or revenue, arising from your use of the Service.
        Our total liability for any claim relating to the Service will not exceed the amount you paid us in the
        12 months before the claim arose.
      </Paragraph>

      <Title level={4}>12. Indemnification</Title>
      <Paragraph>
        You agree to indemnify and hold Hairplane harmless from claims arising from your use of the Service,
        your violation of these Terms, or your violation of any law or third-party right, including claims
        related to communications you send to your Clients through the Service.
      </Paragraph>

      <Title level={4}>13. Changes to These Terms</Title>
      <Paragraph>
        We may update these Terms from time to time. We'll update the "Last updated" date above, and for material
        changes, provide additional notice where appropriate. Continued use of the Service after changes take
        effect means you accept the updated Terms.
      </Paragraph>

      <Title level={4}>14. Governing Law</Title>
      <Paragraph>
        These Terms are governed by the laws of the state in which Hairplane's business is registered, without
        regard to conflict-of-law principles.
      </Paragraph>

      <Title level={4}>15. Contact Us</Title>
      <Paragraph>
        Questions about these Terms can be sent to <Text code>support@hairplane.com</Text>.
      </Paragraph>
    </LegalPageLayout>
  );
}
